import { useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "lucide-react";
import { useShadowPortal } from "~/widgets/lib/use-shadow-portal";
import { BOOKING_PORTAL_IDS } from "@baluu/ui/lib/checkout/portals";
import { Profile, type ProfileProps } from "./Profile";

export type ProfileOverlayProps = ProfileProps;

/**
 * Full-screen overlay wrapping <Profile>, opened by LoginButton.tsx's
 * `openProfileOverlay()` — it creates a bare `<baluu-profile-overlay>`
 * element and appends it to `document.body` (no close handle of its own,
 * unlike lib/checkout-loader.ts's `openCheckout()` which keeps the element
 * it created in a closure and can remove it directly). This component is
 * mounted BY that element's own r2wc-created shadow root, so instead it has
 * to find its own host from the inside to remove it on close.
 *
 * `useShadowPortal` (Task 9, also used by CustomerAuthDialog) is used here
 * deliberately instead of rendering the overlay directly into the r2wc
 * host's own shadow root: `index.ts`'s `BaluuProfileOverlay` class copies
 * `BaluuBookNow`'s connectedCallback (per the brief), which does NOT style
 * the host element as a fixed full-viewport box the way `BaluuCheckoutOverlay`
 * does — so the actual full-screen backdrop+card gets its own dedicated,
 * fixed-positioned shadow-root container instead, the same trick the auth
 * dialog uses to guarantee it paints above everything on the partner page.
 */
export function ProfileOverlay(p: ProfileOverlayProps) {
  const mount = useShadowPortal({ open: true, id: BOOKING_PORTAL_IDS.profile, primaryColor: p.primaryColor });
  // Rendered directly (not through the portal) so it lands inside the
  // <baluu-profile-overlay> element's OWN shadow root — letting handleClose
  // resolve exactly that element via getRootNode().host, rather than the
  // first match of a global `document.querySelector`, which would be wrong
  // if more than one profile overlay ever existed on the page at once.
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    const root = anchorRef.current?.getRootNode();
    const host =
      root instanceof ShadowRoot ? (root.host as HTMLElement) : document.querySelector("baluu-profile-overlay");
    host?.remove();
  }, []);

  return (
    <>
      <div ref={anchorRef} aria-hidden="true" style={{ display: "none" }} />
      {mount &&
        createPortal(
          <div
            className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleClose();
            }}
          >
            <div className="relative my-8 w-full max-w-3xl rounded-xl bg-background shadow-xl">
              <button
                type="button"
                aria-label="Close"
                onClick={handleClose}
                className="absolute right-4 top-4 z-10 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring"
              >
                <XIcon className="size-5" />
              </button>
              <div className="max-h-[85vh] overflow-y-auto p-6">
                <Profile {...p} />
              </div>
            </div>
          </div>,
          mount,
        )}
    </>
  );
}

export default ProfileOverlay;
